import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_FURNITURE_FILL_COLOR
} from "@gettactic/map-builder/src/components/map/defines";
import type { FurnitureShape } from "@gettactic/map-builder/src/types";
import {
  fetchSvgText,
  toDataUri
} from "@gettactic/map-builder/src/utils/svgAssetCache";
import React from "react";
import { buildLocalShapeTransform } from "../shapeTransform";
import { CaptionText } from "./CaptionText";

export interface FurnitureSvgProps {
  shape: FurnitureShape;
}

/**
 * Furniture is fetched once per url (shared session cache) and drawn as an
 * `<image>` stretched to the shape size, like the editor's rasterised
 * Konva image, instead of inlining the asset's nodes for every shape.
 */
export const FurnitureSvg: React.FC<FurnitureSvgProps> = ({ shape }) => {
  const fillColor = shape.color || DEFAULT_FURNITURE_FILL_COLOR;
  const url = shape.furniture?.url;
  const [href, setHref] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!url) return;
    let isCancelled = false;
    fetchSvgText(url)
      .then((svgText) => {
        if (isCancelled) return;
        setHref(toDataUri(svgText.replace(/currentColor/g, fillColor)));
      })
      .catch(() => {
        if (!isCancelled) setHref(null);
      });
    return () => {
      isCancelled = true;
    };
  }, [url, fillColor]);

  return (
    <g transform={buildLocalShapeTransform(shape)}>
      {href ? (
        <image
          href={href}
          width={shape.width}
          height={shape.height}
          preserveAspectRatio="none"
          opacity={shape.locked ? 0.5 : undefined}
        />
      ) : (
        <rect
          width={shape.width}
          height={shape.height}
          fill={fillColor}
          opacity={0.3}
        />
      )}
      {shape.text ? (
        <CaptionText
          text={shape.text}
          width={shape.width}
          top={shape.height + 10}
          textAlign={shape.textAlign}
          fontSize={shape.fontSize || 20}
          fontFamily={shape.fontFamily || DEFAULT_FONT_FAMILY}
          isBold={shape.isBold}
          isItalic={shape.isItalic}
          isUnderline={shape.isUnderline}
          color={shape.textColor || "#B5B5B5"}
        />
      ) : null}
    </g>
  );
};
